import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AdminEditDrawer,
  AdminEmptyState,
  AdminField,
  AdminPageHeader,
  AdminPagination,
  paginateItems,
  previewText,
} from "@/pages/admin/AdminUx";
import { PORTFOLIO_PROJECTS } from "@/data/portfolioProjects";
import { mergeProject } from "@/lib/portfolioCms";
import PortfolioDetailPage from "@/pages/portfolio/PortfolioDetailPage";
import { usePreviewModalChrome } from "@/pages/admin/usePreviewModalChrome";
import { X } from "lucide-react";
import "@/pages/admin/cmsLivePreview.css";

const EMPTY = {
  slug: "",
  title: "",
  tag: "",
  url: "",
  cardImage: "",
  shortDescription: "",
  overview: "",
  keyFeatures: "",
  enabled: true,
  order: 0,
  seo_title: "",
  seo_description: "",
};

export default function PortfolioCmsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const closePreview = useCallback(() => setShowPreview(false), []);
  usePreviewModalChrome(showPreview ? closePreview : undefined);

  const draftProject = () => {
    if (!editing) return null;
    const features = typeof editing.keyFeatures === "string"
      ? editing.keyFeatures.split("\n").map((s) => s.trim()).filter(Boolean)
      : editing.keyFeatures;
    const tags = typeof editing.categoryTags === "string"
      ? editing.categoryTags.split(",").map((s) => s.trim()).filter(Boolean)
      : editing.categoryTags;
    const fallback = PORTFOLIO_PROJECTS.find((row) => row.slug === editing.slug) || null;
    return mergeProject({ ...editing, keyFeatures: features, categoryTags: tags }, fallback) || {
      ...editing,
      keyFeatures: features || [],
      categoryTags: tags || [],
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/portfolio/projects");
      setItems(r.data.items || []);
    } catch {
      toast.error("포트폴리오 로드 실패");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((row) => {
      if (status === "on" && row.enabled === false) return false;
      if (status === "off" && row.enabled !== false) return false;
      if (!needle) return true;
      return `${row.title} ${row.slug} ${row.tag}`.toLowerCase().includes(needle);
    });
  }, [items, q, status]);

  const paged = paginateItems(filtered, page, 10);
  useEffect(() => { setPage(1); }, [q, status]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        ...editing,
        keyFeatures: typeof editing.keyFeatures === "string"
          ? editing.keyFeatures.split("\n").map((s) => s.trim()).filter(Boolean)
          : editing.keyFeatures,
        categoryTags: typeof editing.categoryTags === "string"
          ? editing.categoryTags.split(",").map((s) => s.trim()).filter(Boolean)
          : editing.categoryTags,
      };
      if (editing.id) await api.put(`/admin/portfolio/projects/${editing.id}`, payload);
      else await api.post("/admin/portfolio/projects", payload);
      toast.success("저장되었습니다.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
    setSaving(false);
  };

  const importCode = async () => {
    try {
      for (const project of PORTFOLIO_PROJECTS) {
        if (items.some((row) => row.slug === project.slug)) continue;
        await api.post("/admin/portfolio/projects", {
          ...project,
          order: PORTFOLIO_PROJECTS.indexOf(project),
          enabled: true,
        });
      }
      toast.success("코드 프로젝트를 가져왔습니다.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "가져오기 실패");
    }
  };

  return (
    <div data-testid="admin-portfolio">
      <AdminPageHeader
        title="포트폴리오 관리"
        description="공개 페이지는 CMS 실패 시 기존 코드 데이터로 fallback 됩니다."
        actions={(
          <>
            <button type="button" className="admin-row__btn" onClick={importCode}>코드에서 가져오기</button>
            <button type="button" className="bg-white text-black rounded-lg px-3 py-1.5 text-sm" onClick={() => setEditing({ ...EMPTY })}>
              + 추가
            </button>
          </>
        )}
      />
      <div className="admin-toolbar">
        <input className="admin-toolbar__search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="제목 / slug 검색" data-testid="portfolio-search" />
        <select className="admin-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">전체</option>
          <option value="on">활성</option>
          <option value="off">비활성</option>
        </select>
        <span className="admin-count">{filtered.length}개</span>
      </div>
      {loading ? (
        <div className="text-sm text-white/40">불러오는 중…</div>
      ) : paged.items.length === 0 ? (
        <AdminEmptyState message="등록된 프로젝트가 없습니다. 코드 fallback이 공개 페이지를 유지합니다." action={<button type="button" className="admin-row__btn" onClick={importCode}>코드에서 가져오기</button>} />
      ) : (
        <div className="admin-row-list">
          {paged.items.map((row) => (
            <div key={row.id} className="admin-row" data-testid={`portfolio-row-${row.slug}`}>
              <div className="admin-row__main">
                <div className="admin-row__title">{row.title || row.slug}</div>
                <div className="admin-row__preview">{row.slug} · {row.tag || "—"}</div>
                <div className="admin-row__meta">{row.enabled === false ? "비활성" : "활성"}</div>
              </div>
              <div className="admin-row__actions">
                <button type="button" className="admin-row__btn" onClick={() => setEditing({
                  ...row,
                  keyFeatures: (row.keyFeatures || []).join("\n"),
                  categoryTags: (row.categoryTags || []).join(", "),
                })}>수정</button>
                <button type="button" className="admin-row__btn" onClick={async () => {
                  await api.put(`/admin/portfolio/projects/${row.id}`, { ...row, enabled: row.enabled === false });
                  load();
                }}>{row.enabled === false ? "활성" : "비활성"}</button>
                <button type="button" className="admin-row__btn" onClick={async () => {
                  if (!window.confirm(`${row.title || row.slug} 삭제?`)) return;
                  await api.delete(`/admin/portfolio/projects/${row.id}`);
                  load();
                }}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <AdminPagination page={paged.page} pageSize={10} total={filtered.length} onPageChange={setPage} />

      <AdminEditDrawer
        open={Boolean(editing)}
        title={editing?.id ? "포트폴리오 수정" : "포트폴리오 추가"}
        onClose={() => setEditing(null)}
        footer={(
          <>
            <button type="button" className="admin-row__btn" onClick={() => setShowPreview(true)} data-testid="portfolio-detail-preview">
              상세페이지 미리보기
            </button>
            <button type="button" className="btn-primary" disabled={saving} onClick={save}>{saving ? "저장 중..." : "저장"}</button>
          </>
        )}
      >
        {editing ? (
          <div className="space-y-3">
            <AdminField label="주소 슬러그">
              <input className="admin-input" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="portfolio-slug" />
            </AdminField>
            <AdminField label="제목" hint="사용 위치: 포트폴리오 상세 > 제목">
              <input className="admin-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </AdminField>
            <AdminField label="카테고리" hint="사용 위치: 포트폴리오 상세 > 태그">
              <input className="admin-input" value={editing.tag || ""} onChange={(e) => setEditing({ ...editing, tag: e.target.value })} />
            </AdminField>
            <AdminField label="사이트 URL">
              <input className="admin-input" value={editing.url || ""} onChange={(e) => setEditing({ ...editing, url: e.target.value })} />
            </AdminField>
            <AdminField label="대표 이미지" hint="사용 위치: 포트폴리오 상세 > 대표 이미지">
              <input className="admin-input" value={editing.cardImage || ""} onChange={(e) => setEditing({ ...editing, cardImage: e.target.value })} />
            </AdminField>
            <AdminField label="짧은 설명" hint="사용 위치: 포트폴리오 상세 > 리드 카피">
              <textarea className="admin-input" rows={3} value={editing.shortDescription || ""} onChange={(e) => setEditing({ ...editing, shortDescription: e.target.value })} />
            </AdminField>
            <AdminField label="섹션 콘텐츠" hint="사용 위치: 포트폴리오 상세 > 본문 섹션">
              <textarea className="admin-input" rows={3} value={editing.overview || ""} onChange={(e) => setEditing({ ...editing, overview: e.target.value })} />
            </AdminField>
            <AdminField label="주요 기능 (줄바꿈)">
              <textarea className="admin-input" rows={4} value={editing.keyFeatures || ""} onChange={(e) => setEditing({ ...editing, keyFeatures: e.target.value })} />
            </AdminField>
            <AdminField label="검색 제목">
              <input className="admin-input" value={editing.seo_title || ""} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} />
            </AdminField>
            <AdminField label="검색 설명">
              <textarea className="admin-input" rows={2} value={editing.seo_description || ""} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} />
            </AdminField>
            <AdminField label="순서"><input className="admin-input" type="number" value={editing.order || 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} /></AdminField>
          </div>
        ) : null}
      </AdminEditDrawer>
      {showPreview && editing ? (
        <div className="cms-full-preview" data-testid="portfolio-preview-modal">
          <div className="cms-full-preview__bar">
            <span>상세페이지 미리보기 · 저장 전 draft · LOCAL/DEV</span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closePreview();
              }}
              aria-label="닫기"
              className="cms-full-preview__close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="cms-full-preview__body" onClickCapture={(e) => {
            if (e.target.closest("a, button")) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}>
            <PortfolioDetailPage previewProject={draftProject()} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
